import { type JSX, type ReactNode, useEffect, useRef } from 'react';

export interface LauncherModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Test ID for the outer dialog element. */
  testId?: string;
  /**
   * When provided, renders a "← Back" link in the footer (left of
   * Close). Set by the caller when this modal was opened from another
   * modal — clicking Back pops one level off the navigation stack.
   * Close always dismisses the entire stack regardless.
   */
  onBack?: () => void;
}

/**
 * Centered modal sheet — header with title, scrollable body, footer
 * with "Close" (always) and an optional "← Back" link when the modal
 * was opened from another modal (navigation stack > 1 deep).
 *
 * Closes on Escape and on backdrop click. Focus moves to the Back
 * button on mount when present, otherwise to Close, so keyboard
 * users always have a sensible default to hit Enter on.
 */
export function LauncherModal({
  title,
  onClose,
  children,
  testId,
  onBack,
}: LauncherModalProps): JSX.Element {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const backRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    // Prefer Back when it exists — "go back to where I came from" is
    // the more common intent inside a nested modal. Without Back,
    // Close is the only target.
    (backRef.current ?? closeRef.current)?.focus();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      className="launcher-modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid={testId ?? 'launcher-modal'}
    >
      <div
        className="launcher-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="launcher-modal__head">
          <h2 className="launcher-modal__title">{title}</h2>
        </header>
        <div className="launcher-modal__body">{children}</div>
        <footer className="launcher-modal__foot">
          {onBack !== undefined && (
            <button
              ref={backRef}
              type="button"
              className="launcher-modal__back-link"
              onClick={onBack}
              data-testid="launcher-modal-back"
            >
              ← Back
            </button>
          )}
          <button
            ref={closeRef}
            type="button"
            className="launcher-modal__close-link"
            onClick={onClose}
            data-testid="launcher-modal-close"
          >
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}
